import { useEffect, useState, useRef } from "react";
import {
  Building2, MapPin, Phone, Banknote, Clock, Palette, Mail, Save, Plus, Trash2,
  Upload, Image as ImageIcon, Globe2, FileText, Loader2, Check, AlertCircle,
  ChevronDown, Star,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFirms } from "@/contexts/FirmContext";
import { toast } from "sonner";
import LetterheadEditor from "@/components/LetterheadEditor";
import ModuleHint from "@/components/ModuleHint";
import { EmptyState } from "@/components/Shared";
import { compressImage } from "@/lib/imageCompressor";
import DOMPurify from "dompurify";

const TABS = [
  { key: "stammdaten", label: "Stammdaten", Icon: Building2 },
  { key: "adresse", label: "Adresse & Kontakt", Icon: MapPin },
  { key: "geschaeftsfuehrung", label: "Geschäftsführung", Icon: Phone },
  { key: "bank", label: "Bankverbindungen", Icon: Banknote },
  { key: "oeffnungszeiten", label: "Öffnungszeiten", Icon: Clock },
  { key: "branding", label: "Branding & Logo", Icon: Palette },
  { key: "briefkopf", label: "Briefkopf-Editor", Icon: FileText },
  { key: "signaturen", label: "E-Mail-Signaturen", Icon: Mail },
  { key: "extras", label: "Sonstiges", Icon: Globe2 },
];

const WEEKDAYS = [
  ["monday", "Montag"],
  ["tuesday", "Dienstag"],
  ["wednesday", "Mittwoch"],
  ["thursday", "Donnerstag"],
  ["friday", "Freitag"],
  ["saturday", "Samstag"],
  ["sunday", "Sonntag"],
];

export default function Firmenprofil() {
  const { user } = useAuth();
  const { firms, activeFirmId, setActiveFirmId } = useFirms();
  const [tab, setTab] = useState("stammdaten");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [selectedFirmId, setSelectedFirmId] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "geschaeftsfuehrung" || user?.is_super_admin;

  // Resolve which firm to load:
  //   1. explicit page-level selection (this dropdown)
  //   2. top-bar active firm
  //   3. first firm in the list (typically the default)
  useEffect(() => {
    if (selectedFirmId) return;
    if (activeFirmId && firms.some((f) => f.id === activeFirmId)) {
      setSelectedFirmId(activeFirmId);
    } else if (firms.length > 0) {
      const def = firms.find((f) => f.is_default) || firms[0];
      setSelectedFirmId(def.id);
    }
  }, [firms, activeFirmId, selectedFirmId]);

  async function load(fid) {
    if (!fid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/firms/${fid}/profile`);
      setData(data);
      setSavedAt(null);
    } catch (_e) {
      toast.error("Profil konnte nicht geladen werden");
    } finally { setLoading(false); }
  }
  useEffect(() => { if (selectedFirmId) load(selectedFirmId); }, [selectedFirmId]);

  async function save() {
    if (!selectedFirmId) return;
    setBusy(true);
    try {
      const { data: saved } = await api.put(`/firms/${selectedFirmId}/profile`, data);
      setData(saved);
      setSavedAt(new Date());
      window.dispatchEvent(new Event("company-profile-changed"));
      toast.success("Firmenprofil gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  }

  function setField(k, v) { setData(d => ({ ...d, [k]: v })); }
  function setNested(parentKey, k, v) {
    setData(d => ({ ...d, [parentKey]: { ...(d[parentKey] || {}), [k]: v } }));
  }

  function chooseFirm(fid) {
    setSelectedFirmId(fid);
    // Keep top-bar switcher in sync when user navigates per page
    if (activeFirmId !== fid) setActiveFirmId(fid);
  }

  const selectedFirm = firms.find((f) => f.id === selectedFirmId);

  if (loading || !data) {
    return (
      <div className="p-6 max-w-7xl mx-auto" data-testid="firmenprofil-page">
        <FirmPicker firms={firms} selectedFirmId={selectedFirmId} onChoose={chooseFirm} />
        <div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-zinc-500"/></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="firmenprofil-page">
      <ModuleHint moduleKey="firmenprofil" />
      <FirmPicker firms={firms} selectedFirmId={selectedFirmId} onChoose={chooseFirm}
                  selectedFirm={selectedFirm} isDefault={!!data?.is_default_firm} />
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="w-full lg:w-56 flex-shrink-0">
        <h1 className="text-xl font-display font-bold text-zinc-50 mb-1">Firmenprofil</h1>
        <p className="text-xs text-zinc-500 mb-5">
          {selectedFirm?.name || "Firma wählen"}
          {data?.is_default_firm && <span className="ml-1 text-amber-400">· Standard</span>}
        </p>
        <nav className="space-y-0.5">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 transition ${
                tab === t.key
                  ? "bg-zinc-800 text-zinc-100"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
              }`}>
              <t.Icon className="w-4 h-4 flex-shrink-0"/>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          {tab === "stammdaten" && <SectionStammdaten data={data} setField={setField} disabled={!isAdmin}/>}
          {tab === "adresse" && <SectionAdresse data={data} setField={setField} disabled={!isAdmin}/>}
          {tab === "geschaeftsfuehrung" && <SectionGeschaeftsfuehrung data={data} setField={setField} disabled={!isAdmin}/>}
          {tab === "bank" && <SectionBank data={data} setField={setField} disabled={!isAdmin}/>}
          {tab === "oeffnungszeiten" && <SectionHours data={data} setField={setField} setNested={setNested} disabled={!isAdmin}/>}
          {tab === "branding" && <SectionBranding data={data} setField={setField} reload={() => load(selectedFirmId)} disabled={!isAdmin} firmId={selectedFirmId}/>}
          {tab === "briefkopf" && (
            data?.is_default_firm
              ? <LetterheadEditor profile={data} disabled={!isAdmin} onSaved={(d) => setData(d)} />
              : (
                <div className="text-sm text-zinc-300" data-testid="briefkopf-not-supported">
                  <h2 className="text-lg font-semibold text-zinc-100 mb-1">Briefkopf-Editor</h2>
                  <p className="text-sm text-zinc-500 mb-4">
                    Der visuelle Briefkopf-Editor ist derzeit nur für die Standard-Firma verfügbar.
                  </p>
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded text-amber-200/90">
                    Um den Briefkopf zu bearbeiten, wechseln Sie oben zur Standard-Firma. Logo,
                    Adresse, IBAN und Fußzeile dieser Firma fließen automatisch in deren PDFs ein
                    (Rechnungen, Lenkprotokoll, Lohnzettel).
                  </div>
                </div>
              )
          )}
          {tab === "signaturen" && <SectionSignatures data={data} setField={setField} disabled={!isAdmin}/>}
          {tab === "extras" && <SectionExtras data={data} setField={setField} setNested={setNested} disabled={!isAdmin}/>}
        </div>

        {isAdmin && tab !== "briefkopf" && (
          <div className="mt-4 flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg p-4 sticky bottom-4 backdrop-blur">
            <div className="text-xs text-zinc-500">
              {savedAt ? <span className="inline-flex items-center gap-1.5 text-emerald-400"><Check className="w-3 h-3"/> gespeichert um {savedAt.toLocaleTimeString("de-AT")}</span>
                       : "Änderungen werden nicht automatisch gespeichert"}
            </div>
            <button onClick={save} disabled={busy} data-testid="profile-save"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
              Speichern
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Firm picker — header card with dropdown
function FirmPicker({ firms, selectedFirmId, onChoose, selectedFirm, isDefault }) {
  const [open, setOpen] = useState(false);
  if (!firms || firms.length === 0) return null;
  const f = selectedFirm || firms.find((x) => x.id === selectedFirmId) || firms[0];
  return (
    <div className="mb-5" data-testid="firmenprofil-firm-picker">
      <div className="flex items-center justify-between gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-lg">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md flex-shrink-0 flex items-center justify-center"
               style={{ background: (f?.color || "#3b82f6") + "20", border: `1px solid ${(f?.color || "#3b82f6")}55` }}>
            <Building2 className="w-4 h-4" style={{ color: f?.color || "#3b82f6" }} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Profil bearbeiten für</div>
            <div className="text-sm font-semibold text-zinc-100 truncate flex items-center gap-2">
              <span className="truncate">{f?.name || "—"}</span>
              {isDefault && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-[10px] font-semibold text-amber-300 uppercase tracking-wider">
                  <Star className="w-2.5 h-2.5 fill-current" /> Standard
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="relative">
          <button
            type="button"
            data-testid="firmenprofil-firm-picker-trigger"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded text-sm font-medium transition-colors"
          >
            Firma wechseln
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {open && (
            <>
              <button type="button" onClick={() => setOpen(false)}
                className="fixed inset-0 z-40 cursor-default" tabIndex={-1} aria-hidden />
              <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl py-1.5"
                   data-testid="firmenprofil-firm-picker-menu">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                  Verfügbare Firmen
                </div>
                {firms.map((firm) => (
                  <button
                    key={firm.id}
                    data-testid={`firmenprofil-firm-option-${firm.id}`}
                    onClick={() => { onChoose(firm.id); setOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-800 ${
                      firm.id === selectedFirmId ? "text-zinc-100" : "text-zinc-300"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: firm.color || "#3b82f6" }} />
                    <span className="flex-1 text-left truncate">{firm.name}</span>
                    {firm.is_default && <span className="text-[9px] uppercase text-amber-400 font-semibold">std</span>}
                    {firm.id === selectedFirmId && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────
function L({ label, hint, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}

function I({ value, onChange, disabled, placeholder = "", type = "text", testid, mono }) {
  return (
    <input type={type} value={value || ""} disabled={disabled} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={`w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 disabled:opacity-60 focus:outline-none focus:border-blue-500 ${mono ? "font-mono" : ""}`}/>
  );
}

function SectionStammdaten({ data, setField, disabled }) {
  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">Stammdaten</h2>
      <p className="text-sm text-zinc-500 mb-5">Rechtliche Identität — wird auf Rechnungen und in Behördenkommunikation verwendet.</p>
      <div className="grid grid-cols-2 gap-4">
        <L label="Firmenname (Anzeige) *"><I value={data.name} onChange={(v) => setField("name", v)} disabled={disabled} testid="prof-name"/></L>
        <L label="Vollständiger Rechtsname"><I value={data.legal_name} onChange={(v) => setField("legal_name", v)} disabled={disabled} placeholder="z.B. Munja Trans GmbH"/></L>
        <L label="Rechtsform"><I value={data.legal_form} onChange={(v) => setField("legal_form", v)} disabled={disabled} placeholder="GmbH, AG, e.U."/></L>
        <L label="Buchhaltungs-Nr."><I value={data.accounting_number} onChange={(v) => setField("accounting_number", v)} disabled={disabled}/></L>
        <L label="UID-Nr (USt-IdNr)"><I value={data.uid_number} onChange={(v) => setField("uid_number", v)} disabled={disabled} placeholder="ATU12345678" mono/></L>
        <L label="Firmenbuch-Nr (FN)"><I value={data.fn_number} onChange={(v) => setField("fn_number", v)} disabled={disabled} placeholder="FN 123456 a" mono/></L>
        <L label="EORI-Nummer"><I value={data.eori_number} onChange={(v) => setField("eori_number", v)} disabled={disabled} mono/></L>
        <L label="Zuständiges Finanzamt"><I value={data.tax_office} onChange={(v) => setField("tax_office", v)} disabled={disabled}/></L>
        <L label="Gegründet am"><I value={data.founded_date} onChange={(v) => setField("founded_date", v)} disabled={disabled} type="date"/></L>
      </div>
    </>
  );
}

function SectionAdresse({ data, setField, disabled }) {
  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">Adresse & Kontakt</h2>
      <p className="text-sm text-zinc-500 mb-5">Sitz der Firma + offizielle Kommunikationskanäle.</p>
      <div className="grid grid-cols-2 gap-4">
        <L label="Straße & Hausnummer *" full><I value={data.address_line} onChange={(v) => setField("address_line", v)} disabled={disabled} testid="prof-address"/></L>
        <L label="Adresszusatz" full><I value={data.address_line2} onChange={(v) => setField("address_line2", v)} disabled={disabled} placeholder="Stiege, Tür"/></L>
        <L label="PLZ"><I value={data.plz} onChange={(v) => setField("plz", v)} disabled={disabled}/></L>
        <L label="Stadt"><I value={data.city} onChange={(v) => setField("city", v)} disabled={disabled}/></L>
        <L label="Bundesland"><I value={data.state} onChange={(v) => setField("state", v)} disabled={disabled}/></L>
        <L label="Land"><I value={data.country} onChange={(v) => setField("country", v)} disabled={disabled}/></L>
        <L label="Länder-Code"><I value={data.country_code} onChange={(v) => setField("country_code", v.toUpperCase())} disabled={disabled} placeholder="AT" mono/></L>
        <div></div>
        <L label="Allgemein E-Mail *"><I value={data.email} onChange={(v) => setField("email", v)} disabled={disabled} testid="prof-email" type="email"/></L>
        <L label="Telefon *"><I value={data.phone} onChange={(v) => setField("phone", v)} disabled={disabled} testid="prof-phone"/></L>
        <L label="Mobil"><I value={data.mobile} onChange={(v) => setField("mobile", v)} disabled={disabled}/></L>
        <L label="Fax"><I value={data.fax} onChange={(v) => setField("fax", v)} disabled={disabled}/></L>
        <L label="Support-E-Mail" hint="für Fahrer-Anfragen"><I value={data.support_email} onChange={(v) => setField("support_email", v)} disabled={disabled} type="email"/></L>
        <L label="Buchhaltungs-E-Mail" hint="für Eingangsrechnungen"><I value={data.billing_email} onChange={(v) => setField("billing_email", v)} disabled={disabled} type="email"/></L>
      </div>
    </>
  );
}

function SectionGeschaeftsfuehrung({ data, setField, disabled }) {
  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">Geschäftsführung</h2>
      <p className="text-sm text-zinc-500 mb-5">Verantwortliche Person — erscheint auf Lenkerauskünften & Behördenpost.</p>
      <div className="grid grid-cols-2 gap-4">
        <L label="Vorname *"><I value={data.ceo_first_name} onChange={(v) => setField("ceo_first_name", v)} disabled={disabled} testid="prof-ceo-fname"/></L>
        <L label="Nachname *"><I value={data.ceo_last_name} onChange={(v) => setField("ceo_last_name", v)} disabled={disabled} testid="prof-ceo-lname"/></L>
        <L label="Funktion"><I value={data.ceo_role} onChange={(v) => setField("ceo_role", v)} disabled={disabled} placeholder="Geschäftsführer / Inhaber"/></L>
        <div></div>
        <L label="Direkt-E-Mail"><I value={data.ceo_email} onChange={(v) => setField("ceo_email", v)} disabled={disabled} type="email"/></L>
        <L label="Direkt-Telefon"><I value={data.ceo_phone} onChange={(v) => setField("ceo_phone", v)} disabled={disabled}/></L>
      </div>
    </>
  );
}

function SectionBank({ data, setField, disabled }) {
  const banks = data.bank_accounts || [];
  function add() {
    setField("bank_accounts", [...banks, { label: `Konto ${banks.length + 1}`, bank_name: "", iban: "", bic: "", is_primary: banks.length === 0 }]);
  }
  function update(i, k, v) {
    const next = [...banks];
    next[i] = { ...next[i], [k]: v };
    if (k === "is_primary" && v) next.forEach((b, j) => { if (j !== i) b.is_primary = false; });
    setField("bank_accounts", next);
  }
  function remove(i) { setField("bank_accounts", banks.filter((_, j) => j !== i)); }

  return (
    <>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-zinc-100">Bankverbindungen</h2>
        {!disabled && (
          <button onClick={add} data-testid="prof-bank-add" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-xs rounded">
            <Plus className="w-3.5 h-3.5"/> Neue Bank
          </button>
        )}
      </div>
      <p className="text-sm text-zinc-500 mb-5">Mehrere Konten möglich (z.B. Hauptkonto + Treuhand). Das Hauptkonto erscheint auf Rechnungen.</p>
      {banks.length === 0 && <EmptyState label="Noch keine Bankverbindung hinterlegt." />}
      <div className="space-y-3">
        {banks.map((b, i) => (
          <div key={i} className={`p-4 rounded border ${b.is_primary ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-800 bg-zinc-950"}`}>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3"><L label="Bezeichnung"><I value={b.label} onChange={(v) => update(i, "label", v)} disabled={disabled}/></L></div>
              <div className="col-span-3"><L label="Bankname"><I value={b.bank_name} onChange={(v) => update(i, "bank_name", v)} disabled={disabled}/></L></div>
              <div className="col-span-4"><L label="IBAN"><I value={b.iban} onChange={(v) => update(i, "iban", v.toUpperCase().replace(/\s/g, " "))} disabled={disabled} mono/></L></div>
              <div className="col-span-2"><L label="BIC"><I value={b.bic} onChange={(v) => update(i, "bic", v.toUpperCase())} disabled={disabled} mono/></L></div>
              <div className="col-span-12 flex items-center gap-3 mt-1">
                <label className="flex items-center gap-1.5 text-xs text-zinc-300">
                  <input type="checkbox" checked={!!b.is_primary} onChange={(e) => update(i, "is_primary", e.target.checked)} disabled={disabled}/>
                  als Hauptkonto markieren
                </label>
                {!disabled && <button onClick={() => remove(i)} className="ml-auto text-red-400 hover:text-red-300 text-xs inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5"/> entfernen</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function SectionHours({ data, setField, setNested, disabled }) {
  const bh = data.business_hours || {};
  function setDay(day, k, v) {
    const next = { ...bh, [day]: { ...(bh[day] || {}), [k]: v } };
    setField("business_hours", next);
  }
  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">Öffnungszeiten</h2>
      <p className="text-sm text-zinc-500 mb-5">Zeitzone: <strong className="text-zinc-300">{data.timezone || "Europe/Vienna"}</strong></p>
      <div className="space-y-2">
        {WEEKDAYS.map(([key, label]) => {
          const d = bh[key] || {};
          return (
            <div key={key} className="grid grid-cols-12 gap-3 items-center py-1.5 border-b border-zinc-800">
              <div className="col-span-3 text-sm text-zinc-200">{label}</div>
              <div className="col-span-2">
                <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <input type="checkbox" checked={!!d.closed} onChange={(e) => setDay(key, "closed", e.target.checked)} disabled={disabled}/>
                  geschlossen
                </label>
              </div>
              <div className="col-span-3"><I type="time" value={d.start} onChange={(v) => setDay(key, "start", v)} disabled={disabled || d.closed}/></div>
              <div className="col-span-1 text-center text-xs text-zinc-600">bis</div>
              <div className="col-span-3"><I type="time" value={d.end} onChange={(v) => setDay(key, "end", v)} disabled={disabled || d.closed}/></div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function SectionBranding({ data, setField, reload, disabled, firmId }) {
  const fileLogoRef = useRef(null);
  const fileFavRef = useRef(null);
  const fileInvRef = useRef(null);

  async function uploadBranding(kind, file) {
    // Phase 5Z.42 — compress to ≤1920px (logos are small but users upload huge originals)
    // Logos may have transparency → keep PNG; favicon stays small anyway
    const compressed = await compressImage(file, {
      maxDimension: 1920,
      jpegQuality: 0.9,
      keepPngForTransparency: true,
    });
    const fd = new FormData(); fd.append("file", compressed);
    try {
      await api.post(`/firms/${firmId}/branding/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      window.dispatchEvent(new Event("company-profile-changed"));
      toast.success("Logo hochgeladen");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    }
  }
  async function deleteBranding(kind) {
    if (!window.confirm("Wirklich entfernen?")) return;
    await api.delete(`/firms/${firmId}/branding/${kind}`);
    window.dispatchEvent(new Event("company-profile-changed"));
    toast.success("Entfernt");
    await reload();
  }
  const cacheBust = data.updated_at || "";
  const base = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = data.logo_path ? `${base}/api/firms/${firmId}/branding/logo?_t=${cacheBust}` : "";
  const favUrl = data.favicon_path ? `${base}/api/firms/${firmId}/branding/favicon?_t=${cacheBust}` : "";
  const invUrl = data.invoice_logo_path ? `${base}/api/firms/${firmId}/branding/invoice_logo?_t=${cacheBust}` : "";

  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">Branding & Logo</h2>
      <p className="text-sm text-zinc-500 mb-5">Wird beim Login, in der Sidebar und auf Rechnungen dieser Firma angezeigt.</p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <L label="Primärfarbe (Buttons, Akzente)" hint="Hex-Code">
          <div className="flex items-center gap-2">
            <input type="color" value={data.primary_color || "#2563EB"} onChange={(e) => setField("primary_color", e.target.value)} disabled={disabled} className="w-12 h-10 bg-zinc-950 border border-zinc-800 rounded cursor-pointer"/>
            <I value={data.primary_color} onChange={(v) => setField("primary_color", v)} disabled={disabled} testid="prof-color-primary" mono/>
          </div>
        </L>
        <L label="Akzentfarbe">
          <div className="flex items-center gap-2">
            <input type="color" value={data.accent_color || "#10B981"} onChange={(e) => setField("accent_color", e.target.value)} disabled={disabled} className="w-12 h-10 bg-zinc-950 border border-zinc-800 rounded cursor-pointer"/>
            <I value={data.accent_color} onChange={(v) => setField("accent_color", v)} disabled={disabled} mono/>
          </div>
        </L>
      </div>

      {/* Logo Upload Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <LogoCard label="Logo (Sidebar + Login)" url={logoUrl} hint="Empfohlen: 512×512 PNG mit Transparenz. Max 4 MB."
          onUpload={(f) => uploadBranding("logo", f)} onDelete={() => deleteBranding("logo")} disabled={disabled} fileRef={fileLogoRef} testid="logo-card"/>
        <LogoCard label="Favicon" url={favUrl} hint="32×32 oder 64×64 PNG/ICO. Max 2 MB." square
          onUpload={(f) => uploadBranding("favicon", f)} onDelete={() => deleteBranding("favicon")} disabled={disabled} fileRef={fileFavRef} testid="favicon-card"/>
        <LogoCard label="Logo für Rechnungen" url={invUrl} hint="Größere Variante für PDF-Header. 1024×1024 PNG."
          onUpload={(f) => uploadBranding("invoice_logo", f)} onDelete={() => deleteBranding("invoice_logo")} disabled={disabled} fileRef={fileInvRef} testid="invoice-logo-card"/>
      </div>

      <div className="mt-5 p-4 bg-zinc-950 border border-zinc-800 rounded text-sm">
        <div className="text-xs text-zinc-500 mb-2">Live-Vorschau</div>
        <div className="flex items-center gap-3">
          {logoUrl && <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded object-contain bg-zinc-900 p-1"/>}
          <div>
            <div className="text-zinc-100 font-semibold">{data.name || "Ihre Firma"}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80">Transport-Plattform</div>
          </div>
          <button className="ml-auto px-4 py-1.5 text-white text-sm rounded" style={{ background: data.primary_color || "#C28535" }}>Anmelden</button>
        </div>
      </div>
    </>
  );
}

function LogoCard({ label, url, hint, square, onUpload, onDelete, disabled, fileRef, testid }) {
  return (
    <div className={`bg-zinc-950 border border-zinc-800 rounded p-4 ${square ? "" : ""}`} data-testid={testid}>
      <div className="text-xs text-zinc-300 mb-2">{label}</div>
      <div className="aspect-square bg-zinc-900 rounded mb-3 flex items-center justify-center overflow-hidden">
        {url ? <img src={url} alt={label} className="max-w-full max-h-full object-contain"/> : <ImageIcon className="w-8 h-8 text-zinc-700"/>}
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}/>
      <div className="flex items-center gap-2">
        <button onClick={() => fileRef.current?.click()} disabled={disabled}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-xs rounded disabled:opacity-50">
          <Upload className="w-3.5 h-3.5"/> {url ? "Ersetzen" : "Hochladen"}
        </button>
        {url && !disabled && (
          <button onClick={onDelete} className="px-2 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs rounded inline-flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
      {hint && <p className="text-[10px] text-zinc-600 mt-2 leading-snug">{hint}</p>}
    </div>
  );
}

function SectionSignatures({ data, setField, disabled }) {
  const SIGS = [
    ["default_email_signature_html", "Standard", "Wird verwendet, wenn keine modul-spezifische Signatur gesetzt ist."],
    ["invoice_email_signature_html", "Rechnungen", "Wird an alle Rechnungs-E-Mails angehängt."],
    ["lenkerauskunft_signature_html", "Lenkerauskunft", "Erscheint unter jeder Lenkerauskunft-Antwort."],
    ["buchhaltung_signature_html", "Buchhaltung", "Für An-/Abmeldungen + Stundenmeldungen."],
  ];
  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">E-Mail-Signaturen</h2>
      <p className="text-sm text-zinc-500 mb-5">HTML erlaubt — &lt;b&gt;, &lt;a&gt;, &lt;br&gt;, etc. Wird an passende Modul-Mails angehängt.</p>
      <div className="space-y-5">
        {SIGS.map(([key, label, hint]) => (
          <div key={key}>
            <div className="flex items-baseline justify-between mb-1">
              <label className="text-xs uppercase tracking-wider text-zinc-300">{label}</label>
              <span className="text-[10px] text-zinc-600">{hint}</span>
            </div>
            <textarea value={data[key] || ""} onChange={(e) => setField(key, e.target.value)} disabled={disabled} rows={5}
              data-testid={`sig-${key}`}
              placeholder={`<p>Mit freundlichen Grüßen<br><b>${data.name || "Ihre Firma"}</b></p>`}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 font-mono leading-relaxed disabled:opacity-60"/>
            {(data[key] || "").trim() && (
              <div className="mt-2 p-3 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-300">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Vorschau</div>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data[key] || "") }}/>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function SectionExtras({ data, setField, setNested, disabled }) {
  return (
    <>
      <h2 className="text-lg font-semibold text-zinc-100 mb-1">Weitere Informationen</h2>
      <p className="text-sm text-zinc-500 mb-5">Marketing-Texte und Social-Media-Links.</p>
      <div className="grid grid-cols-2 gap-4">
        <L label="Über uns (öffentlich)" full>
          <textarea value={data.public_about || ""} onChange={(e) => setField("public_about", e.target.value)} disabled={disabled} rows={4}
            placeholder="Kurzbeschreibung des Unternehmens — kann auf der Login-Seite angezeigt werden."
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100"/>
        </L>
        <L label="Interne Notizen (privat)" full>
          <textarea value={data.notes || ""} onChange={(e) => setField("notes", e.target.value)} disabled={disabled} rows={3}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100"/>
        </L>
        <L label="Webseite"><I value={data.social?.website} onChange={(v) => setNested("social", "website", v)} disabled={disabled} placeholder="https://"/></L>
        <L label="Facebook"><I value={data.social?.facebook} onChange={(v) => setNested("social", "facebook", v)} disabled={disabled}/></L>
        <L label="Instagram"><I value={data.social?.instagram} onChange={(v) => setNested("social", "instagram", v)} disabled={disabled}/></L>
        <L label="LinkedIn"><I value={data.social?.linkedin} onChange={(v) => setNested("social", "linkedin", v)} disabled={disabled}/></L>
      </div>
    </>
  );
}
